import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import { getMatrixModeStatus, getMatrixTimeMessage } from '../utils/timeUtils';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const [matrixMode, setMatrixMode] = useState({ isActive: false, reason: 'normal', message: '' });
  const [showMatrixText, setShowMatrixText] = useState(false);

  useEffect(() => {
    // Check Matrix mode status
    const status = getMatrixModeStatus();
    setMatrixMode(status);

    // Show Matrix text after a brief delay if in Matrix mode
    if (status.isActive) {
      const timer = setTimeout(() => {
        setShowMatrixText(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Render Matrix version if Matrix mode is active
  if (matrixMode.isActive) {
    return (
      <div className="min-h-screen bg-black flex flex-col relative overflow-hidden">
        {/* Matrix Rain Background Effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="matrix-rain">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="matrix-column"
                style={{
                  left: `${i * 5}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                }}
              >
                {Array.from({ length: 20 }).map((_, j) => (
                  <span key={j} className="matrix-char text-green-400 font-mono text-sm">
                    {String.fromCharCode(0x30A0 + Math.random() * 96)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <Header />
        
        <main className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Hero Section */}
            <div className="mb-12">
              {/* Large 404 Text */}
              <div className="mb-8">
                <h1 className="text-9xl md:text-[12rem] font-bold text-transparent bg-gradient-to-r from-green-400 to-green-600 bg-clip-text leading-none font-mono">
                  404
                </h1>
                <div className="relative -mt-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-green-600/20 blur-3xl"></div>
                </div>
              </div>

              {/* Matrix Error Message */}
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-green-400 mb-4 font-mono">
                  You've been unplugged from the Matrix
                </h2>
                {showMatrixText && (
                  <div className="space-y-2 animate-fade-in">
                    <p className="text-xl text-green-300 mb-2 font-mono">
                      {getMatrixTimeMessage()}
                    </p>
                    <p className="text-lg text-green-500/80 font-mono">
                      {matrixMode.message}
                    </p>
                  </div>
                )}
              </div>

              {/* Matrix-themed message */}
              <div className="bg-black/80 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-green-500/30 relative">
                <div className="absolute top-2 left-2 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <div className="absolute top-2 left-8 w-3 h-3 bg-green-400/60 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute top-2 left-14 w-3 h-3 bg-green-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                
                <div className="flex items-center justify-center mb-4 mt-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                </div>
                <p className="text-green-300 text-lg font-mono">
                  The page you seek does not exist in this reality...
                </p>
              </div>
            </div>

            {/* Navigation Options - Matrix Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {/* Red Pill - Home */}
              <button
                onClick={handleGoHome}
                className="group bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-red-500/50"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                    </svg>
                  </div>
                  <span className="font-semibold font-mono">Red Pill</span>
                  <span className="text-sm opacity-90">Return to reality</span>
                </div>
              </button>

              {/* Blue Pill - Live TV */}
              <button
                onClick={() => navigate('/live-tv')}
                className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-blue-500/50"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="relative">
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8z"/>
                      </svg>
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <span className="font-semibold font-mono">Blue Pill</span>
                  <span className="text-sm opacity-90">Stay in the simulation</span>
                </div>
              </button>

              {/* Green Pill - Movies */}
              <button
                onClick={() => navigate('/on-demand')}
                className="group bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-green-500/50"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                    </svg>
                  </div>
                  <span className="font-semibold font-mono">Green Pill</span>
                  <span className="text-sm opacity-90">Enter the code</span>
                </div>
              </button>

              {/* White Pill - Series */}
              <button
                onClick={() => navigate('/search')}
                className="group bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-gray-500/50"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/>
                    </svg>
                  </div>
                  <span className="font-semibold font-mono">White Pill</span>
                  <span className="text-sm opacity-90">Choose your path</span>
                </div>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={handleGoBack}
                className="px-8 py-3 bg-black/80 hover:bg-black border border-green-500/50 hover:border-green-400 text-green-400 rounded-xl transition-all duration-300 transform hover:scale-105 font-mono"
              >
                ← Jack back in
              </button>
              <button
                onClick={handleGoHome}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl transition-all duration-300 transform hover:scale-105 font-semibold font-mono"
              >
                Follow the white rabbit
              </button>
            </div>

            {/* Additional Help */}
            <div className="mt-12 text-center">
              <p className="text-green-500/60 text-sm font-mono">
                "There is no spoon... but there is Sports.TV."
              </p>
            </div>
          </div>
        </main>

        <Footer />

        <style>{`
          @keyframes matrix-fall {
            0% { transform: translateY(-100vh); opacity: 1; }
            100% { transform: translateY(100vh); opacity: 0; }
          }
          
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          .matrix-rain {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
          }
          
          .matrix-column {
            position: absolute;
            top: -100vh;
            width: 20px;
            height: 100vh;
            animation: matrix-fall linear infinite;
            display: flex;
            flex-direction: column;
            justify-content: space-around;
          }
          
          .matrix-char {
            display: block;
            opacity: 0.7;
          }
          
          .animate-fade-in {
            animation: fade-in 1s ease-out;
          }
        `}</style>
      </div>
    );
  }

  // Render normal version if not Matrix time
  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Hero Section */}
          <div className="mb-12">
            {/* Large 404 Text */}
            <div className="mb-8">
              <h1 className="text-9xl md:text-[12rem] font-bold text-transparent bg-gradient-to-r from-[#FCC834] to-[#E0A800] bg-clip-text leading-none">
                404
              </h1>
              <div className="relative -mt-8">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FCC834]/20 to-[#E0A800]/20 blur-3xl"></div>
              </div>
            </div>

            {/* Error Message */}
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Oops! Page Not Found
              </h2>
              <p className="text-xl text-white/80 mb-2">
                Looks like this content took a commercial break...
              </p>
              <p className="text-lg text-white/60">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </div>

            {/* Fun TV-themed message */}
            <div className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-[#FCC834]/30">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-[#FCC834] to-[#E0A800] rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8zM9 10v4l4-2-4-2z"/>
                  </svg>
                </div>
              </div>
              <p className="text-white/90 text-lg">
                Don't worry! There's plenty of great content waiting for you.
              </p>
            </div>
          </div>

          {/* Navigation Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {/* Home */}
            <button
              onClick={handleGoHome}
              className="group bg-gradient-to-r from-[#FCC834] to-[#E0A800] hover:from-[#E0A800] hover:to-[#FCC834] text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
                <span className="font-semibold">Home</span>
                <span className="text-sm opacity-90">Browse featured content</span>
              </div>
            </button>

            {/* Live TV */}
            <button
              onClick={() => navigate('/live-tv')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#FCC834]/30 hover:border-[#FCC834]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <div className="relative">
                  <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8z"/>
                  </svg>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                </div>
                <span className="font-semibold">Live TV</span>
                <span className="text-sm opacity-90">Watch live channels</span>
              </div>
            </button>

            {/* Movies */}
            <button
              onClick={() => navigate('/on-demand')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#FCC834]/30 hover:border-[#FCC834]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                </svg>
                <span className="font-semibold">On Demand</span>
                <span className="text-sm opacity-90">Browse the catalog</span>
              </div>
            </button>

            {/* Series */}
            <button
              onClick={() => navigate('/search')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#FCC834]/30 hover:border-[#FCC834]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/>
                </svg>
                <span className="font-semibold">Search</span>
                <span className="text-sm opacity-90">Find content</span>
              </div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={handleGoBack}
              className="px-8 py-3 bg-secondary/80 hover:bg-secondary border border-[#FCC834]/30 hover:border-[#FCC834]/60 text-white rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              ← Go Back
            </button>
            <button
              onClick={handleGoHome}
              className="px-8 py-3 bg-gradient-to-r from-[#FCC834] to-[#E0A800] hover:from-[#E0A800] hover:to-[#FCC834] text-white rounded-xl transition-all duration-300 transform hover:scale-105 font-semibold"
            >
              Take Me Home
            </button>
          </div>

          {/* Additional Help */}
          <div className="mt-12 text-center">
            <p className="text-white/60 text-sm">
              Still having trouble? Try refreshing the page or check your internet connection.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFoundPage;

